import Backbone from "backbone";
import { webViewLayer as webViewLayerImpl } from "./utils/webViewLayer";
import * as errorHandler from "./utils/error";
import * as logger from "./utils/logger";
import * as debug from "./utils/debug";
import AppView, { IAppViewOptions } from "./app.view";
import * as nativeContainer from "./utils/native/nativeContainer";
import serviceWorker from "./utils/serviceWorker";
import { buildStore, DriveStore } from "./store/reducers";
import { ErrorActions } from "./store/error/actions";
import * as Sentry from "@sentry/browser";
import { log as internalLog, error as internalErrorLog, event as internalEventLog, initiateInstanceId } from "./utils/logger.internal";
import { UserService } from "./utils/users";
import { dispatcher, DispatcherEvents } from "./utils/dispatcher";
import { IApiError } from "./utils/api.interfaces";
import { IOfflineErrorPayload } from "./store/error/types";
import { FeatureFlagName } from "./utils/featureFlagName";
import { getDatabaseNameFromNonUriSource, getDatabaseNameFromUri, IUrl } from "./utils/databaseNameParsingUtilities";
import isFeatureFlagsEnabled from "./isFeatureFlagsEnabled";
import { FeatureFlags } from "./services/featureFlags";
import { initReact } from "../src";
import PerformanceProfilerFactory from "./utils/performanceProfiler/performanceProfilerFactory";
import { MeasureName } from "./utils/performanceProfiler/measureName";
import { isAllowThirdPartyLoggingEnabled } from "./utils/serverFlags";
import constants from "./constants";
import IndexedDbMigrationFromRedux from "./utils/storage/indexedDbMigrationFromRedux";
import { isIphone } from "./utils/native/nativeHelpers";
import { resetViewportZoom } from "./utils/viewportHelper";
import {
    startDriverEvents,
    startDrivingChangeEvents,
    startHosStatusChangeEvents,
    startThemeChangeEvents,
    startVehicleChangeEvents
} from "./utils/native/addinLifecycleEvents";

// The maximum time to wait before showing the login dialog on first app load
const SERVICE_WORKER_INIT_TIMEOUT = 60 * 1000;
// The time to wait after a first time app download error before attempting to download the service worker again
const SERVICE_WORKER_ERROR_RETRY_TIMEOUT = 3 * 60 * 1000;
const APPCACHE_SUCCESS_EVENTS = ["cached", "error", "noupdate", "obsolete", "updateready"];
const NO_AUTH_FEATURE_FLAGS = [
    FeatureFlagName.ENABLE_FORCE_USE_EPHEMERAL_FOR_SAML_LOGIN_WITH_AS,
    FeatureFlagName.ENABLE_INDEXED_DB_STORE,
    FeatureFlagName.ENABLE_SENTRY_TRACING,
    FeatureFlagName.ENABLE_APP_AUTH_IAM_LOGIN_POPUP,
    FeatureFlagName.DISABLE_PENDO_ANALYTICS,
    FeatureFlagName.ENABLE_TESLA_QR_LOGIN
];

/**
 * Responsible for launching the Geotab Drive app's primary `AppView` view/container.
 * Also sets various `window` event listeners and native MobileSDK references
 */
export default class Launcher {
    store: DriveStore;
    private userService: UserService | undefined;
    private readonly startTime = performance.now();
    private downloadStartTime: number = new Date().getTime();
    /**
     * Doesn't seem useful, but IIRC in the past there were difficulties with SW and AppCache working
     * on the same page. This can likely be removed with refactoring
     */
    private appStartedLoading = false;
    private offlineCacheDisabledError: IOfflineErrorPayload | undefined;
    private serviceworkerTimeoutError: IOfflineErrorPayload | undefined;
    private serviceworkerFailedError: IOfflineErrorPayload | undefined;

    // Start promise ASAP to detect if launching within mobile Android/iOS device
    private readonly nativeDeviceReadyPromise = new Promise<string>((resolve) => {
        if (navigator.userAgent.indexOf("MobileSDK") === -1) {
            resolve(`nativeDeviceReady but not in MobileSDK`);
            return;
        }

        /**
         * In the iframe (Cordova) deviceready is never called.
         * So, Cordova initialization is removed from deviceready event
         **/
        let intervalId: number | null = null;
        let timeoutId: number | null = null;
        document.addEventListener("deviceready", () => {
            intervalId && window.clearInterval(intervalId);
            timeoutId && window.clearTimeout(timeoutId);
            let endTime = performance.now();
            resolve(`deviceready event is fired in ${ ((endTime - this.startTime) / 1000).toFixed(2) } seconds`);
        }, false);

        // If deviceready event is missed, we have to check `window.geotabModules` using `window.setInterval`
        if (!window.geotabModules) {
            intervalId = window.setInterval(() => {
                if (window.geotabModules) {
                    intervalId && window.clearInterval(intervalId);
                    timeoutId && window.clearTimeout(timeoutId);
                    let endTime = performance.now();
                    resolve(`nativeDeviceReadyPromise is resolved in ${ ((endTime - this.startTime) / 1000).toFixed(2) } seconds`);
                }
            }, 2000);
            /**
             * `window.setInterval` should not run forever if `window.geotabModules` is undefined.
             * So, cancel the interval inside window.setTimeout
             */
            timeoutId = window.setTimeout(() => {
                intervalId && window.clearInterval(intervalId);
                let endTime = performance.now();
                resolve(`window.geotabModules are undefined after ${ ((endTime - this.startTime) / 1000).toFixed(2) } seconds`);
            }, 10000);
            return;
        }
        // If `window.geotabModules` exists it could be that we missed it and screen is blank/spinning.
        // "deviceready" may trigger after this however, so not certain this is a failure.
        logger.log(logger.Source.NATIVE, "Possible native device ready failure");
    });

    constructor() {
        // Handle message passthrough for logging from a public logging class to the internal one.
        // Registered here (not in initializeApp) so it fires exactly once — Launcher is a singleton.
        // userService may be undefined at this point; handleLoggerLog handles that defensively.
        dispatcher.on(DispatcherEvents.LoggerLog, this.handleLoggerLog.bind(this), this);

        /**
         * Before navigating away try to dump in-memory log cache.
         * Doesn't get fired if driver shuts app down
         * Doesn't upload data to BQ, just makes it permanently available for next upload
         */
        window.addEventListener("beforeunload", () => {
            logger.log(logger.Source.LAUNCHER, `Reload event detected. Stack:${ new Error().stack }`);
        });

        !window.serviceWorkerEnabled && window.applicationCache && document.addEventListener("UILoaded", this.triggerAppCacheDownloadEvent.bind(this), false);

        debug.initDebugMode();

        // Enable logging by default in dev
        if (window.DRIVE_DEBUG && /(.|^)\s*(localhost|127.0.0.1)/.test(window.location.host) && typeof jest === "undefined") {
            window.DRIVE_DEBUG.ENABLE_LOGGING = true;
        }
    }

    /**
     * Begin initializing the app in terms of offline source code caching
     * and connecting to MobileSDK native app.
     */
    initializeApp(): Promise<void> {
        if (!this.appStartedLoading && window.serviceWorkerEnabled && "serviceWorker" in navigator) {
            return this.serviceWorkerInitialization();
        } else if (!this.appStartedLoading && !window.serviceWorkerEnabled && window.applicationCache &&
            (window.applicationCache.status === window.applicationCache.DOWNLOADING || window.applicationCache.status === window.applicationCache.CHECKING)) {
            // Wait for application cache to finish downloading before loading app
            return this.applicationCacheInitialization();
        } else {
            return this.webInitialization();
        }
    }

    /**
     * Checks a few sources for what database the user needs to be on
     * and checks the current `location` to see if the user needs to be redirected
     * @returns string | null The URI string that needs to be redirected to. If null then don't redirect.
     */
    getNewUrlBasedOnDatabaseName(url: IUrl): string | null {
        let redirectDatabase = (database: string) => {
            let basePage = "default.html";
            if (url.pathname.indexOf("/drive/default-legacy.html") > -1) {
                basePage = "default-legacy.html";
            }

            basePage = `/${ database }/drive/${ basePage }`;
            window.__drive_redirect = true;
            let href = url.href;
            return href.replace(url.pathname, basePage);
        };

        if (getDatabaseNameFromUri(url, true, this.store) !== "drive") {
            return null;
        }

        let databaseNameAndMessage = getDatabaseNameFromNonUriSource(url, this.store);
        if (databaseNameAndMessage) {
            logger.log(logger.Source.LAUNCHER, databaseNameAndMessage.message);
            return redirectDatabase(databaseNameAndMessage.databaseName);
        }

        return null;
    }

    private triggerAppCacheDownloadEvent() {
        let downloadTotalTime = (new Date().getTime() - this.downloadStartTime) / 1000;
        let details = { "detail": downloadTotalTime };
        let event;

        if (typeof CustomEvent === "function") {
            event = new CustomEvent("AppCacheDownloaded", details);
        } else {
            event = document.createEvent("CustomEvent");
            event.initCustomEvent("CustomEvent", false, false, details);
        }

        document.dispatchEvent(event);
    }

    private sendToErrorTracker(error: Error, tags: string[]): void {
        let errorToSend = error;
        Sentry.withScope((scope) => {
            scope.setTag("type", ["app/main"].concat(tags).join(","));
            const version = window.geotabModules?.device.version;
            version && scope.setTag("app.release", version);
            scope.setTag("database", this.store.getState().data.lastDatabase);
            errorToSend.message = errorHandler.cleanupSensitiveString(error.message);
            Sentry.captureException(errorToSend);
        });
    }

    /**
     * Handler for `DispatcherEvents.LoggerLog` messages to pipe it into logger.internal.ts which
     * is responsible for logging to memory buffer and eventually disk
     * @param type
     * @param from
     * @param message
     * @param user
     * @param duration Specific to `LogType.EVENT` messages
     * @param connectionType Specific to `LogType.EVENT` messages
     * @param shouldLimitSize Specific to `LogType.ERROR` messages and if true will truncate the error message
     * @param errorObject Specific to `LogType.ERROR`
     */
    private handleLoggerLog(
        type: logger.LogType,
        from: logger.Event | logger.Source,
        message: string | string[],
        user?: string | any,
        duration: number | any = null,
        connectionType: string | any = null,
        shouldLimitSize = true,
        errorObject?: IApiError
    ) {
        const allUsers = this.userService?.getUsersObject() ?? {};
        const currentUser = user ?? this.userService?.getCurrentUser() ?? null;
        switch (type) {
            case logger.LogType.ERROR:
                internalErrorLog(from as logger.Source, message, allUsers, currentUser, shouldLimitSize, errorObject);
                break;
            case logger.LogType.EVENT:
                internalEventLog(from as logger.Event, message, allUsers, currentUser, duration, connectionType);
                break;
            case logger.LogType.LOG:
            default:
                internalLog(from as logger.Source, message, allUsers, currentUser);
                break;
        }
    }

    /**
     * Kickstart the whole app (redirect, native SDK, error tracker, `AppView`, legacy Cordova)
     * @returns void
     */
    private async loadApp() {
        this.store = await buildStore();
        this.userService = new UserService(this.store);
        let featureFlagService = new FeatureFlags(this.store);

        featureFlagService.observeFlagChanges(FeatureFlagName.ENABLE_INDEXED_DB_STORE, () => {
            IndexedDbMigrationFromRedux.migrateIfNeeded(this.store);
        });

        await featureFlagService.downloadNoAuthFeatureFlags(NO_AUTH_FEATURE_FLAGS);
        await IndexedDbMigrationFromRedux.migrateIfNeeded(this.store);

        // ErrorTracker from default.ts
        if (!window.errorTracker) {
            logger.error(logger.Source.LAUNCHER, "Error tracker not initialized");
        } else {
            window.errorTracker.setLogger((type, message) => this.handleLoggerLog({"log": logger.LogType.LOG, "error": logger.LogType.ERROR}[type], logger.Source.ERROR_TRACKER, message));

            // Configure addin error filtering based on feature flag
            window.errorTracker.setAddinErrorFilter(() => !featureFlagService.hasFeatureFlag(FeatureFlagName.REPORT_ADDIN_ERRORS));

            // Set a provider that reads the current page name from Redux for addin error detection
            window.errorTracker.setCurrentPageProvider(() => this.store.getState().mobile.currentPage);

            // Set a provider that reads version info live from Redux for APM spans and transactions
            window.errorTracker.setVersionAttributesProvider(() => {
                const storeState = this.store.getState().data;
                return {
                    schemaVersion: constants.APP_VERSION,
                    databaseVersion: storeState.databaseVersion,
                    sourceCodeVersion: storeState.sourceCodeVersion,
                    nativeVersion: storeState.clientVersion || "",
                    serverFlags: storeState.serverFlags
                };
            });

            await window.errorTracker.configureTracingIntegration(
                isAllowThirdPartyLoggingEnabled(this.store.getState().data.serverFlags),
                featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_SENTRY_TRACING)
            );
        }

        // On iOS, re-trigger migrateIfNeeded each time the app returns to the foreground.
        // Subscribes to Redux state so mobile.background is guaranteed up to date when
        // migrateIfNeeded reads it — avoids a race with app.view.ts dispatching setBackground
        // on the same StatusBackground event. It's a fast no-op if already migrated.
        let prevBackground = this.store.getState().mobile.background;
        this.store.subscribe(() => {
            const currentBackground = this.store.getState().mobile.background;
            if (prevBackground && !currentBackground && isIphone()) {
                IndexedDbMigrationFromRedux.migrateIfNeeded(this.store);
                resetViewportZoom();
            }
            prevBackground = currentBackground;
        });

        // dispatch error in starting up after the store is initialized
        if (this.offlineCacheDisabledError) {
            this.store.dispatch(ErrorActions.setOfflineError(this.offlineCacheDisabledError));
        } else if (!this.appStartedLoading && this.serviceworkerTimeoutError) {
            this.store.dispatch(ErrorActions.setOfflineError(this.serviceworkerTimeoutError));
        } else if (this.serviceworkerFailedError) {
            this.store.dispatch(ErrorActions.setOfflineError(this.serviceworkerFailedError));
        }
        let redirectUrl = this.getNewUrlBasedOnDatabaseName(window.location);
        if (redirectUrl) {
            // Spent a lot of time debugging DatabaseNameUrlLoginTest
            // and discovered that is a window.location.replace is performed
            // it doesn't fully stop execution and can get to a point
            // where localStorage is manipulated! Must RETURN.
            window.location.replace(redirectUrl);
            return;
        }

        /**
         * For iOS devices
         * @param degree - UIInterfaceOrientationPortrait: 0, UIInterfaceOrientationLandscapeRight: 90, UIInterfaceOrientationLandscapeLeft: -90, UIInterfaceOrientationPortraitUpsideDown: 180
         * @returns Indicating if rotation should be allowed.
         */
        window.shouldRotateToOrientation = function() {
            return true;
        };
        window.webViewLayer = webViewLayerImpl;
        try {
            nativeContainer.initializeNativeSDK(this.sendToErrorTracker.bind(this));
        } catch (error) {
            this.sendToErrorTracker(error, ["nativeUnhandledException"]);
        }
        const el = document.getElementById("app");
        let viewParams: IAppViewOptions = {
            viewOptions: { el },
            store: this.store,
            featureFlagService: featureFlagService,
            userService: this.userService,
            errorTracker: window.errorTracker   // From default.ts
        };

        // Important line to instantiate the Drive app
        window.driveApp = new AppView(viewParams);
        // Single app-wide producer for api.mobile.on("hosStatusChange").
        startHosStatusChangeEvents(this.store, this.userService);
        // Single app-wide producer for api.mobile.on("themeChange").
        startThemeChangeEvents(this.store);
        // Single app-wide producers for api.mobile.on("driverLogin" | "driverLogout" | "driverSeatChange" | "vehicleChange").
        startDriverEvents(this.store);
        startVehicleChangeEvents(this.store);
        // Single app-wide producer for api.mobile.on("drivingChange").
        startDrivingChangeEvents(this.store);
        initiateInstanceId(true);

        // These methods need `AppView` to have been created
        Backbone.history.start();
        errorHandler.listenForJavaScriptErrors();
        window.onunhandledrejection = errorHandler.handleUnhandledRejection;

        if (self !== top) {
            /** There is an existing issue of window.cordova is undefined
             * https://jira.geotab.com/browse/MYG-19689
             **/
            try {
                if (!nativeContainer.initializeCordovaDevice(this.sendToErrorTracker.bind(this))) {
                    window.setTimeout(() => {
                        nativeContainer.initializeCordovaDevice(this.sendToErrorTracker.bind(this));
                    }, 30000);
                }
            } catch (error) {
                this.sendToErrorTracker(error, ["nativeUnhandledException"]);
            }
        }

        let offlineError = this.store.getState().error.offlineError;
        if (offlineError) {
            logger.error(logger.Source.ERROR, JSON.stringify(offlineError), () => {
                const activeUsers = this.store.getState().activeUsers as DeepReadonlyArray<string>;
                return activeUsers.length > 0 ? activeUsers[0] : undefined;
            });
            this.store.dispatch(ErrorActions.clearOfflineError());
        }
        initReact(document.getElementById("root"));
    }

    /**
     * Set up listeners and handling for Service Workers
     * Expects `navigator.serviceWorker` to be defined
     * Triggers `this.loadApp` if successful
     */
    private async serviceWorkerInitialization() {
        let serviceWorkerInitProfiler = PerformanceProfilerFactory.getInstance(MeasureName.ServiceWorkerInit);
        serviceWorkerInitProfiler.start();

        let done = () => {
            if (this.appStartedLoading) {
                return;
            }
            this.appStartedLoading = true;
            this.nativeDeviceReadyPromise.then(async message => {
                await this.loadApp().then(() => {
                    logger.log(logger.Source.NATIVE, `SERVICEWORKER: ${ message }`);
                    serviceWorkerInitProfiler.end();
                });
            });
        };

        let failed = (error: Error, extraDetails?: {[key: string]: any}) => {
            if (error) {
                this.serviceworkerFailedError = {
                    message: error.message,
                    tags: ["offline", "serviceworker"],
                    extraDetails: extraDetails,
                    userAgent: window.navigator.userAgent
                };
            }
            done();
        };

        let initServiceWorker = (): Promise<void> => {
            logger.log(logger.Source.LAUNCHER, "Initialize service worker");
            void Promise.race([
                // navigator.serviceWorker.ready never rejects:
                // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/ready
                navigator.serviceWorker.ready,

                // ... however, it's possible that a catastrophic error has occurred with the service worker, so just let the
                // driver into the app in a minute, and we'll try to go offline again soon after the app loads
                new Promise<boolean>(resolve => setTimeout(() => {
                    resolve(false);
                }, SERVICE_WORKER_INIT_TIMEOUT))
            ]).then((serviceWorkerActivated: boolean | ServiceWorkerRegistration) => {
                if (!serviceWorkerActivated) {
                    this.serviceworkerTimeoutError = {
                        message: "ServiceWorker timeout on app load",
                        tags: ["offline", "serviceworker"],
                        userAgent: window.navigator.userAgent
                    };
                }
                done();
            });

            return serviceWorker.init()
                .then((reg: ServiceWorkerRegistration) => {
                    reg.installing?.addEventListener("statechange", function () {
                        if (!navigator.serviceWorker.controller && this.state === "redundant") {
                            // On first install, if we fail to precache (for missing files, etc.), we end up here
                            let retryDateString = `${ new Date(new Date().getTime() + SERVICE_WORKER_ERROR_RETRY_TIMEOUT).toString() }`;
                            failed(new Error(`Couldn't install new service worker and no current workers`), {
                                willRetryAt: retryDateString
                            });

                            // Try again to initialize/go offline again in a few minutes
                            setTimeout(() => {
                                void serviceWorker.init();
                            }, SERVICE_WORKER_ERROR_RETRY_TIMEOUT);
                        }
                    });
                }).catch(reason => {
                    failed(new Error(reason));
                });
        };

        // cache.keys() returns an array of Request objects
        const cacheNames = await window.caches.keys();

        // Get the keys (Request objects) for all caches
        const keysPerCachePromise = cacheNames.map(async (key) => {
            try {
                const cache = await window.caches.open(key);

                // AWAIT HERE: Ensure the outer promise resolves with the array itself
                const keys = await cache.keys();
                return keys;
            } catch (e) {
                logger.error(logger.Source.ERROR, `Cannot get the cache key request object - ${ e?.message }`);
                return [];
            }
        });

        const nestedKeys = await Promise.all(keysPerCachePromise);

        // Filter the Request array to check if file name exists
        // This stops iterating the moment 'some' condition returns true.
        const hasFileInCache = nestedKeys.some(requests => requests.find(request => request.url.toLowerCase().includes("launcher.pack.js")));
        if (hasFileInCache) {
            logger.log(logger.Source.LAUNCHER, "File in Cache - Loaded app before service worker");
            done();
             // Check if the page is already fully loaded
            if (document.readyState === "complete") {
                initServiceWorker();
            } else {
                logger.log(logger.Source.LAUNCHER, "Waiting for DOMContentLoaded to initialize service worker");
                document.addEventListener("DOMContentLoaded", initServiceWorker);
            }
            return Promise.resolve();
        } else {
            return initServiceWorker();
        }
    }

    /**
     * Set up listeners for applicationCache events.
     * Triggers `this.loadApp` if successful
     * @deprecated No desktop or mobile browser supports ApplicationCache. Old packaged Drive installs do still have it however.
     */
    private applicationCacheInitialization(): Promise<void> {
        let appCacheSuccess = (evt: Event) => {
            if (!this.appStartedLoading) {
                this.appStartedLoading = true;
                this.nativeDeviceReadyPromise.then(async message => {
                    await this.loadApp().then(() => {
                        logger.log(logger.Source.NATIVE, `APPCACHE: ${ message }`);
                    });
                });
                window.applicationCache.removeEventListener(evt.type, this);
            } else {
                for (let event of APPCACHE_SUCCESS_EVENTS) {
                    window.applicationCache.removeEventListener(event, this);
                }
            }
        };

        return new Promise((resolve) => {
            for (let event of APPCACHE_SUCCESS_EVENTS) {
                // Is Event time logging required
                if (event === "cached" || event === "updateready") {
                    this.downloadStartTime = new Date().getTime();
                }
                window.applicationCache.addEventListener(event, appCacheSuccess.bind(this), false);
            }
            resolve();
        });
    }

    /**
     * Standard web initialization if not handled by Service Workers or Application Cache
     */
    private webInitialization(): Promise<void> {
        if (!this.appStartedLoading || !window.applicationCache || window.applicationCache.status === window.applicationCache.UNCACHED) {
            // If app wasn't kicked off by the above methods, or doesn't have appCache present/enabled, send it with additional info to error tracker
            let tags = ["offline"];
            if (window.applicationCache) {
                // Extra tag to be able to specifically filter AppCache issues when the browser supports it, but is not properly offlining
                tags.push("appCache");
            }
            this.offlineCacheDisabledError = {
                message: "Offline caching is disabled",
                tags: tags,
                serviceWorkerEnabled: !!window.serviceWorkerEnabled,
                serviceWorkerCompatible: "serviceWorker" in navigator,
                applicationCacheStatus: window.applicationCache?.status || (window.applicationCache ? "Unknown" : "No AppCache Support"),
                userAgent: window.navigator.userAgent
            };
        }
        return this.nativeDeviceReadyPromise.then(async message => {
            await this.loadApp().then(() => {
                logger.log(logger.Source.NATIVE, `WEB: ${ message }`);
            });
        });
    }
}

// Make available to default and default-legacy HTML pages
window.launcher = Launcher;

/**
 * Utility for allowing calls to the unauthenticated "GetFeatureFlag" endpoint. Made available
 * everywhere via 'window.isFeatureFlagsEnabled'
 *
 * Attempts to find & use the current database name in the request through various means, such as -
 * 1. Directly from the URL, like 'my.geotab.com/myDatabaseName/'
 * 2. Parse it from a credentials string passed as an argument to the url, like 'my.geotab.com/drive/#login,(credentials: database: 'myDatabaseName', etc..)'
 * 3. (if redux store is available) Parse it from credentials stored in Redux under 'driveApiCredentials'
 * 4. (if redux store is available) Parse it from Redux under 'lastDatabase'
 *
 * Within isFeatureFlagsEnabled, the utility will cache according to the following:
 * 1. If there is no store available, local storage will be **written to** and **read from**
 * 2. If there is a redux store, the store will be **checked** but not written to. The preference when writing to FeatureFlag store is
 * to use the functions within FeatureFlags.ts
 *
 * @param flag The name of the Feature Flag whose status we want to check
 * @param store If available from where we are calling this method, we can pass in an instance of the Drive redux store. This allows us
 * more options for our attempts to parse the database name and check the store for existing feature flags if fetch fails
 * @returns An asynchronous call to the unauthenticated 'GetFeatureFlag' endpoint. Resolves out with a IFeatureFlagStatus representing whether or not
 * the provided Feature Flag is enabled
 */
window.isFeatureFlagsEnabled = async (flags: FeatureFlagName[], store?: DriveStore, database?: string) => {
    if (database) {
        return isFeatureFlagsEnabled(flags, database, store);
    }

    // Grab the DB name if available
    let url = window.location as IUrl;
    let databaseName: string | null = "";
    let databaseNameFromUri = getDatabaseNameFromUri(url, false, store);
    if (databaseNameFromUri.length) {
        databaseName = databaseNameFromUri;
    } else {
        let databaseNameFromNonUriSource = getDatabaseNameFromNonUriSource(url, store);
        if (databaseNameFromNonUriSource) {
            databaseName = databaseNameFromNonUriSource.databaseName;
        }
    }

    return isFeatureFlagsEnabled(flags, databaseName, store);
};